/*eslint-disable*/
import React from "react";
import {
  useTable,
  useFilters,
  useSortBy,
  usePagination,
} from "react-table";
import classnames from "classnames";
// A great library for fuzzy filtering/sorting items
import { matchSorter } from "match-sorter";
// react plugin used to create DropdownMenu for selecting items
// reactstrap components
import { FormGroup, Input } from "reactstrap";
// Import color utilities for theme colors
import { getActiveColorStyle, getButtonColor } from "../../utils/colorUtils";
import { getTheme2SearchFilterSingleSelectStyles } from "../../utils/theme2MultiSelect";
// Import custom select component
import Select from "../Select";
import { useTranslation } from "react-i18next";

/** Page indices are 0-based; returns mixed items { type: 'page'|'ellipsis', index?, key? } */
function buildPaginationItems(pageIndex, pageCount) {
  const total = Math.max(1, pageCount || 1);
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => ({ type: "page", index: i }));
  }
  const current = pageIndex + 1;
  const set = new Set([1, total]);
  for (let p = current - 1; p <= current + 1; p += 1) {
    if (p >= 1 && p <= total) set.add(p);
  }
  const sorted = [...set].sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i += 1) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      out.push({ type: "ellipsis", key: `e-${sorted[i - 1]}-${sorted[i]}` });
    }
    out.push({ type: "page", index: sorted[i] - 1 });
  }
  return out;
}

// Define a default UI for filtering
function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;

  return (
    <FormGroup>
      <Input
        type="email"
        value={filterValue || ""}
        onChange={(e) => {
          setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
        }}
        placeholder={`Search ${count} records...`}
      />
    </FormGroup>
  );
}

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;

// Our table component
function Table({ columns, data, onRowClick, activeColor = "secondary", className, disableViewportClamp = false, renderExpandedRow, isRowExpanded, manualPagination = false, pageCount: externalPageCount, pageIndex: externalPageIndex, onPageChange, onPageSizeChange, pageSize: externalPageSize, totalItems, noDataText, loading = false, loadingText, paginationSelectClassName = "react-select rt-page-size-select", paginationSelectStyles, compactPagination = false, paginationMetaExtra = null }) {
  const { t } = useTranslation();
  const [numberOfRows, setNumberOfRows] = React.useState(10);

  const accentColor = getButtonColor(activeColor);
  const activeColorStyle = React.useMemo(
    () => getActiveColorStyle(activeColor),
    [activeColor]
  );
  const [isLightTheme, setIsLightTheme] = React.useState(() =>
    typeof document !== "undefined" && document.body.classList.contains("white-content")
  );

  React.useEffect(() => {
    const syncTheme = () => {
      setIsLightTheme(document.body.classList.contains("white-content"));
    };
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const activePageButtonStyle = React.useMemo(
    () => ({
      borderColor: activeColorStyle.borderColor,
      background: isLightTheme ? "rgba(15, 23, 42, 0.08)" : "rgba(255, 255, 255, 0.14)",
      color: isLightTheme ? activeColorStyle.borderColor : "#ffffff",
      fontWeight: 600,
    }),
    [activeColorStyle.borderColor, isLightTheme]
  );
  const defaultPaginationSelectStyles = React.useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true),
    [activeColorStyle]
  );
  const resolvedPaginationSelectStyles =
    paginationSelectStyles || defaultPaginationSelectStyles;

  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    visibleColumns,
    nextPage,
    pageOptions,
    pageCount,
    previousPage,
    canPreviousPage,
    canNextPage,
    setPageSize,
    gotoPage,
  } = useTable(
    {
      columns,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      initialState: { pageSize: externalPageSize || 10, pageIndex: externalPageIndex || 0 },
      manualPagination,
      pageCount: manualPagination ? externalPageCount : undefined,
      autoResetPage: !manualPagination,
      autoResetExpanded: false,
      autoResetGroupBy: false,
      autoResetSelectedRows: false,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetRowState: false,
    },
    useFilters, // useFilters!
    useSortBy,
    usePagination
  );

  // Sync external page index to internal table and dropdown when in manual mode.
  // Clamp to [0, pageCount-1]. Do NOT list state.pageIndex in deps — gotoPage updates it and would retrigger this effect forever.
  // Parent pages (e.g. SearchInventory) must clamp `page` vs server pageCount separately.
  React.useEffect(() => {
    if (!manualPagination || typeof externalPageIndex !== "number" || !Number.isFinite(externalPageIndex)) return;
    const safeCount =
      typeof externalPageCount === "number" && externalPageCount > 0 ? externalPageCount : 1;
    const maxIndex = Math.max(0, safeCount - 1);
    const clampedIndex = Math.min(Math.max(0, externalPageIndex), maxIndex);

    if (clampedIndex !== state.pageIndex) {
      gotoPage(clampedIndex);
    }
    // gotoPage identity changes most renders; listing it retriggers this effect and can cause "Maximum update depth exceeded".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPageIndex, externalPageCount, manualPagination]);

  // Keep the page size dropdown label in sync with external page size
  React.useEffect(() => {
    if (typeof externalPageSize !== 'number') return;
    setNumberOfRows(externalPageSize);
    if (externalPageSize !== state.pageSize) {
      setPageSize(externalPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPageSize]);

  // Note: we call onPageSizeChange directly from the dropdown onChange to avoid feedback loops

  // We don't want to render all of the rows for this example, so cap
  // it for this use case
  const numberOfRowsData = [5, 10, 20, 25, 50, 100];

  const isManualMode = manualPagination && typeof externalPageIndex === "number";
  const currentPageIndex = isManualMode ? externalPageIndex : state.pageIndex;
  const effectivePageCount = manualPagination ? (externalPageCount || 1) : (pageCount || 1);
  const canGoPrev = currentPageIndex > 0;
  const canGoNext = currentPageIndex + 1 < effectivePageCount;

  const paginationItems = React.useMemo(
    () => buildPaginationItems(currentPageIndex, effectivePageCount),
    [currentPageIndex, effectivePageCount]
  );

  const rangeLabel = React.useMemo(() => {
    const pageSizeVal = state.pageSize;
    const pageIdx = currentPageIndex;
    const rowsOnPage = page.length;
    const totalKnown = typeof totalItems === "number" && totalItems >= 0;
    const resultKey = compactPagination
      ? "common.dataTable.resultOfCompact"
      : "common.dataTable.resultOf";
    const showingKey = compactPagination
      ? "common.dataTable.showingRangeCompact"
      : "common.dataTable.showingRange";

    if (totalKnown && totalItems === 0) {
      return t(resultKey, { start: 0, end: 0, total: 0 });
    }
    if (!manualPagination && data.length === 0) {
      return t(resultKey, { start: 0, end: 0, total: 0 });
    }

    if (totalKnown) {
      const start = totalItems === 0 ? 0 : pageIdx * pageSizeVal + 1;
      const end = totalItems === 0 ? 0 : Math.min((pageIdx + 1) * pageSizeVal, totalItems);
      return t(resultKey, { start, end, total: totalItems });
    }

    if (!manualPagination) {
      const total = data.length;
      const start = total === 0 ? 0 : pageIdx * pageSizeVal + 1;
      const end = total === 0 ? 0 : Math.min((pageIdx + 1) * pageSizeVal, total);
      return t(resultKey, { start, end, total });
    }

    if (rowsOnPage === 0) {
      return t(showingKey, { start: 0, end: 0 });
    }
    const start = pageIdx * pageSizeVal + 1;
    const end = pageIdx * pageSizeVal + rowsOnPage;
    return t(showingKey, { start, end });
  }, [t, state.pageSize, currentPageIndex, page.length, totalItems, manualPagination, data.length, compactPagination]);

  const goToPageIndex = (idx) => {
    if (isManualMode && typeof onPageChange === "function") {
      onPageChange(idx);
      return;
    }
    gotoPage(idx);
  };

  return (
    <>
      <div
        className={classnames(
          "ReactTable -striped -highlight",
          !disableViewportClamp && "rt-table-in-viewport",
          className
        )}
      >
        <div
          className={classnames("table-scroll-wrapper", {
            "rt-scroll-body": !disableViewportClamp,
          })}
          style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}
        >
          <table {...getTableProps()} className="rt-table" style={{ width: "100%" }}>
            <thead className="rt-thead -header">
              {headerGroups?.map((headerGroup, i) => (
                <tr {...headerGroup.getHeaderGroupProps()} className="rt-tr" key={i}>
                  {headerGroup.headers.map((column, key) => (
                    <th
                      key={key}
                      {...column.getHeaderProps([{
                        style: {
                          width: column.width,
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth,
                          backgroundColor:
                            "var(--rt-header-bg, var(--theme-custom, " + accentColor + "))",
                          color: "var(--rt-header-text, #fff)",
                        }
                      }, column.getSortByToggleProps()])}
                      className={classnames("rt-th rt-resizable-header", {
                        "-cursor-pointer": headerGroup.headers.length - 1 !== key,
                        "-sort-asc": column.isSorted && !column.isSortedDesc,
                        "-sort-desc": column.isSorted && column.isSortedDesc,
                      })}
                    >
                      <div className="rt-resizable-header-content" style={{ color: "var(--rt-header-text, #fff)" }}>
                        {column.render("Header")}
                      </div>
                      {/* Render the columns filter UI */}
                      <div>
                        {/* {headerGroup.headers.length - 1 === key
                          ? null
                          : column.canFilter
                            ? column.render("Filter")
                            : null} */}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()} className="rt-tbody">
              {loading && (
                <tr className="rt-tr rt-tr--empty">
                  <td
                    className="rt-td rt-td--empty"
                    colSpan={Math.max(visibleColumns.length, 1)}
                    style={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      height: `${(state.pageSize || 10) * 48}px`,
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    <div
                      className="d-inline-flex align-items-center"
                      style={{ gap: 10, color: "var(--rt-pagination-accent, " + activeColorStyle.borderColor + ")" }}
                    >
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                        style={{ color: "var(--rt-pagination-accent, " + activeColorStyle.borderColor + ")" }}
                      />
                      <span>{loadingText || t("common.loading", { defaultValue: "Loading..." })}</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && (!page || page.length === 0) && (
                <tr className="rt-tr rt-tr--empty">
                  <td
                    className="rt-td rt-td--empty"
                    colSpan={Math.max(visibleColumns.length, 1)}
                    style={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      height: `63vh`,
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {noDataText || t("common.noRecordsFound")}
                  </td>
                </tr>
              )}
              {!loading && page?.map((row, i) => {
                prepareRow(row);
                return (
                  <React.Fragment key={row.id || i}>
                    <tr
                      {...row.getRowProps()}
                      className={classnames(
                        "rt-tr",
                        { " -odd": i % 2 === 0 },
                        { " -even": i % 2 === 1 }
                      )}
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                      style={{ cursor: 'pointer !important' }}
                    >
                      {row.cells.map((cell) => {
                        return (
                          <td
                            {...cell.getCellProps([{
                              style: {
                                width: cell.column.width,
                                minWidth: cell.column.minWidth,
                                maxWidth: cell.column.maxWidth,
                                whiteSpace: cell.column.whiteSpace, // Optional addition for clean truncations
                                overflow: cell.column.whiteSpace === 'nowrap' ? 'hidden' : undefined,
                                textOverflow: cell.column.whiteSpace === 'nowrap' ? 'ellipsis' : undefined
                              }
                            }])}
                            className="rt-td"
                          >
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                    {renderExpandedRow && isRowExpanded && isRowExpanded(row.original) && (
                      <tr className="rt-tr">
                        <td className="rt-td p-0" colSpan={visibleColumns.length}>
                          {renderExpandedRow(row.original)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="pagination-top">
          <div
            className={classnames("-pagination rt-pagination-modern", {
              "rt-pagination-modern--compact": compactPagination,
            })}
          >
            <div className="rt-pagination-modern__left">
              <span className="rt-pagination-modern__meta">{rangeLabel}</span>
              <span title={t("common.dataTable.perPageTitle")}>
                <Select
                  activeColor={activeColor}
                  value={{
                    value: numberOfRows,
                    label: String(numberOfRows),
                  }}
                  onChange={(option) => {
                    const newSize = option.value;
                    if (manualPagination && typeof onPageSizeChange === "function") {
                      onPageSizeChange(newSize);
                    }
                    setNumberOfRows(newSize);
                    setPageSize(newSize);
                  }}
                  options={numberOfRowsData.map((prop) => ({
                    value: prop,
                    label: String(prop),
                  }))}
                  className={paginationSelectClassName}
                  styles={resolvedPaginationSelectStyles}
                  placeholder="10"
                  isSearchable={false}
                  closeMenuOnSelect={true}
                />
              </span>
              {paginationMetaExtra ? (
                <div className="rt-pagination-modern__extra">{paginationMetaExtra}</div>
              ) : null}
            </div>
            <div className="rt-pagination-modern__right">
              <button
                type="button"
                className="rt-pagination-modern__btn"
                onClick={() => {
                  if (isManualMode && typeof onPageChange === "function") {
                    onPageChange(currentPageIndex - 1);
                    return;
                  }
                  previousPage();
                }}
                disabled={isManualMode ? !canGoPrev : !canPreviousPage}
                title={t("common.dataTable.previous")}
                aria-label={t("common.dataTable.previous")}
              >
                <i className="tim-icons icon-minimal-left" aria-hidden />
                {!compactPagination ? (
                  <span>{t("common.dataTable.previous")}</span>
                ) : null}
              </button>
              {paginationItems.map((item) =>
                item.type === "ellipsis" ? (
                  <span key={item.key} className="rt-pagination-modern__ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={item.index}
                    type="button"
                    className={classnames("rt-pagination-modern__btn", {
                      "rt-pagination-modern__btn--active": item.index === currentPageIndex,
                    })}
                    onClick={() => goToPageIndex(item.index)}
                    style={
                      item.index === currentPageIndex ? activePageButtonStyle : undefined
                    }
                  >
                    {item.index + 1}
                  </button>
                )
              )}
              <button
                type="button"
                className="rt-pagination-modern__btn"
                onClick={() => {
                  if (isManualMode && typeof onPageChange === "function") {
                    onPageChange(currentPageIndex + 1);
                    return;
                  }
                  nextPage();
                }}
                disabled={isManualMode ? !canGoNext : !canNextPage}
                title={t("common.dataTable.next")}
                aria-label={t("common.dataTable.next")}
              >
                {!compactPagination ? (
                  <span>{t("common.dataTable.next")}</span>
                ) : null}
                <i className="tim-icons icon-minimal-right" aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Define a custom filter filter function!
function filterGreaterThan(rows, id, filterValue) {
  return rows.filter((row) => {
    const rowValue = row.values[id];
    return rowValue >= filterValue;
  });
}

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = (val) => typeof val !== "number";

export default Table;
